import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  useColorModeValue,
  Container,
  Alert,
  AlertIcon,
  FormErrorMessage,
  Stack,
  Link as ChakraLink,
  Flex,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { FiMail } from 'react-icons/fi';
import { LOGO_URL, APP_NAME } from '../constants/branding';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset errors before validation
    setEmailError('');
    
    // Validate form
    let hasError = false;
    
    if (!email) {
      setEmailError('Email is required');
      hasError = true;
    } else if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      hasError = true;
    }
    
    if (hasError) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8889'}/auth/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          frontend_url: window.location.origin,
        }),
      });
      
      if (response.ok) {
        setIsSuccess(true);
        setEmail('');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to request password reset');
      }
    } catch (error) {
      console.error('Error requesting password reset:', error);
      // We don't show specific errors to prevent user enumeration
      setIsSuccess(true); // Always show success for security reasons
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg="gray.50"
      position="relative"
      overflow="hidden"
    >
      {/* Background Design */}
      <Box
        position="absolute"
        top="-10%"
        right="-10%"
        width="600px"
        height="600px"
        bg="blue.500"
        borderRadius="full"
        opacity={0.1}
      />
      <Box
        position="absolute"
        bottom="-15%"
        left="-15%"
        width="800px"
        height="800px"
        bg="blue.400"
        borderRadius="full"
        opacity={0.1}
      />

      <Container maxW="sm" position="relative" zIndex={1}>
        <VStack spacing={6} bg="white" p={8} borderRadius="xl" boxShadow="lg">
          <VStack spacing={2} w="full" align="center">
            <Box mb={1}>
              <img 
                src={LOGO_URL}
                alt="Logo" 
                style={{ height: '50px', width: 'auto' }} 
              />
            </Box>
            <Heading size="lg" color="blue.600">Reset Password</Heading>
            <Text color="gray.600" fontSize="md">Get back into your {APP_NAME} account</Text>
          </VStack>

          {isSuccess ? (
            <Stack spacing={4} w="full">
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                If an account exists with that email, we've sent instructions to reset your password.
              </Alert>
              <Button 
                colorScheme="blue" 
                size="md" 
                width="full"
                as={Link} 
                to="/login"
              >
                Back to Login
              </Button>
            </Stack>
          ) : (
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <VStack spacing={4} w="full">
                <Text fontSize="sm" color="gray.600">
                  Enter your email address and we'll send you a link to reset your password.
                </Text>
                
                <Box w="full">
                  <Text mb={1} fontWeight="medium" fontSize="sm" color="gray.600">
                    Email Address
                  </Text>
                  <FormControl isInvalid={!!emailError}>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your email address"
                      variant="filled"
                      size="md"
                    />
                    {emailError && <FormErrorMessage>{emailError}</FormErrorMessage>}
                  </FormControl>
                </Box>
                
                <Button
                  type="submit"
                  colorScheme="blue"
                  size="md"
                  width="full"
                  isLoading={isSubmitting}
                  loadingText="Sending..."
                  mt={2}
                >
                  Send Reset Link
                </Button>
                
                <Text align="center" w="full" fontSize="sm" color="gray.600">
                  Remember your password? <ChakraLink as={Link} to="/login" color="blue.500">Log in</ChakraLink>
                </Text>
              </VStack>
            </form>
          )}
        </VStack>
      </Container>
    </Flex>
  );
};

export default ForgotPassword; 